(() => {
  const scales = {
    sessions: [0, 1000, 5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000, 2500000, 5000000],
    products: [0, 50, 100, 250, 500, 1000, 2500, 5000, 10000],
    collections: [0, 1, 5, 10, 20, 40, 60, 100, 250, 500],
    countries: [0, 100]
  };
  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
  const finite = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;

  function resolveLocale(languages, fallback = 'en', country = 'US') {
    for (const language of [...languages, fallback, 'en-US']) {
      try {
        const locale = new Intl.Locale(language);
        return (locale.region ? locale : new Intl.Locale(locale, {region: country})).toString();
      } catch { /* Try the next available locale. */ }
    }
    return 'en-US';
  }

  function numberCodec(locale) {
    const options = {useGrouping: 'always', maximumFractionDigits: 12};
    const formatter = new Intl.NumberFormat(locale, options);
    const integer = new Intl.NumberFormat(locale, {...options, maximumFractionDigits: 0});
    const parts = formatter.formatToParts(1234567.89);
    const group = parts.find(part => part.type === 'group')?.value || ',';
    const decimal = parts.find(part => part.type === 'decimal')?.value || '.';
    const digitFormat = new Intl.NumberFormat(locale, {useGrouping: false});
    const digits = new Map(Array.from({length: 10}, (_, digit) => [digitFormat.format(digit), String(digit)]));
    const normalize = value => [...value].map(char => digits.get(char) ?? char).join('').replace(/[\u061c\u200e\u200f]/g, '');
    return {
      format: value => formatter.format(value),
      parse(value) {
        let text = normalize(String(value)).trim();
        if (/\s/u.test(group)) text = text.replace(/[\s\u00a0\u202f]/gu, group);
        if (!text) return NaN;
        const pieces = text.split(decimal);
        if (pieces.length > 2 || pieces[1] !== undefined && !/^\d*$/.test(pieces[1])) return NaN;
        const whole = pieces[0].split(group).join('');
        if (!/^\d*$/.test(whole) || !whole && !pieces[1]) return NaN;
        // Validate grouping rather than silently interpreting a misplaced separator.
        if (pieces[0].includes(group) && normalize(integer.format(Number(whole))) !== pieces[0]) return NaN;
        return Number(`${whole || '0'}.${pieces[1] || '0'}`);
      }
    };
  }

  function valueFromPosition(name, position) {
    const stops = scales[name];
    const offset = clamp(position, 0, 1000) / 1000 * (stops.length - 1);
    const index = Math.min(stops.length - 2, Math.floor(offset));
    return Math.round(stops[index] + (stops[index + 1] - stops[index]) * (offset - index));
  }

  function positionFromValue(name, value) {
    const stops = scales[name];
    const index = stops.findIndex((stop, i) => i < stops.length - 1 && value <= stops[i + 1]);
    if (index < 0) return 1000;
    return clamp((index + (value - stops[index]) / (stops[index + 1] - stops[index])) / (stops.length - 1) * 1000, 0, 1000);
  }

  function suggestedReach(collections) {
    if (collections <= 0) return 0;
    const points = [[1, 20], [5, 25], [10, 30], [20, 35], [40, 40], [60, 45]];
    for (let i = 1; i < points.length; i++) {
      const [previousCount, previousShare] = points[i - 1];
      const [count, share] = points[i];
      if (collections <= count) return previousShare + (share - previousShare) * (collections - previousCount) / (count - previousCount);
    }
    return 45;
  }

  function opportunityFactor({products, collections, countries}) {
    // Scenario weights, not fitted merchant coefficients; bounded between 0.85 and 1.15.
    return .85 + .10 * products / (products + 300) + .15 * countries / (countries + 8) + .05 * collections / (collections + 20);
  }

  function calculate(input, shares = [8, 15]) {
    const numbers = [input.sessions, input.products, input.collections, input.countries, input.reach, ...input.rps, ...shares];
    if (!numbers.every(finite) || input.rps.length !== 2 || shares.length !== 2) throw new RangeError('Invalid calculator input');
    const [rpsLow, rpsHigh] = [...input.rps].sort((a, b) => a - b);
    if (input.rates && (input.rates.length !== 2 || !input.rates.every(rate => finite(rate) && rate <= 100))) throw new RangeError('Invalid conversion rate');
    const factor = opportunityFactor(input);
    const shareCap = input.rates ? Math.min(.95, 1 - Math.max(...input.rates) / 100) : .95;
    const [shareLow, shareHigh] = shares.map(value => clamp(value * factor / 100, 0, shareCap)).sort((a, b) => a - b);
    const reach = input.products && input.collections && input.countries ? clamp(input.reach, 0, 100) / 100 : 0;
    const sessions = input.sessions * reach;
    const baseline = [input.sessions * rpsLow, input.sessions * rpsHigh];
    const affectedBaseline = [sessions * rpsLow, sessions * rpsHigh];
    // Incremental share is a fraction of attributed revenue, not of the baseline.
    const attributed = [affectedBaseline[0] / (1 - shareLow), affectedBaseline[1] / (1 - shareHigh)];
    const extra = [affectedBaseline[0] * shareLow / (1 - shareLow), affectedBaseline[1] * shareHigh / (1 - shareHigh)];
    const uplift = extra.map((value, index) => baseline[index] > 0 ? value / baseline[index] * 100 : 0);
    // With unchanged AOV, relative RPS lift equals relative conversion-rate lift.
    const conversionLift = extra.map((value, index) => affectedBaseline[index] > 0 ? value / affectedBaseline[index] * 100 : 0);
    return {sessions, baseline, attributed, extra, uplift, conversionLift, factor, reach: reach * 100, shares: [shareLow * 100, shareHigh * 100]};
  }

  const model = {calculate, suggestedReach, opportunityFactor, valueFromPosition, positionFromValue, resolveLocale, numberCodec};
  globalThis.GeosortRevenueModel = model;
  if (typeof window === 'undefined' || !window.customElements || customElements.get('geosort-revenue-calculator')) return;

  class RevenueCalculator extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.manualReach = false;
      this.labels = JSON.parse(this.dataset.labels || '{}');
      this.inputs = Object.fromEntries([...this.querySelectorAll('[data-grc-value]')].map(input => [input.dataset.grcValue, input]));
      this.locale = resolveLocale(navigator.languages || [navigator.language], this.dataset.locale, this.dataset.country);
      this.codec = numberCodec(this.locale);
      Object.values(this.inputs).forEach(input => {
        if (!input.dataset.grcLocalized) {
          input.value = this.codec.format(Number(input.value));
          input.dataset.grcLocalized = 'true';
        }
      });
      this.querySelectorAll('[data-grc-conversion] option').forEach(option => {
        if (option.value !== 'exact') option.textContent = option.value.split(',').map(value => this.codec.format(Number(value))).join('-') + '%';
      });
      this.querySelectorAll('[data-grc-endpoint]').forEach(element => {
        element.textContent = this.codec.format(Number(element.dataset.grcEndpoint)) + '+';
      });
      this.sliders = Object.fromEntries([...this.querySelectorAll('[data-grc-slider]')].map(input => [input.dataset.grcSlider, input]));
      this.reachInput = this.querySelector('[data-grc-reach]');
      this.currency = this.querySelector('[data-grc-currency]');
      this.error = this.querySelector('[data-grc-error]');
      this.error.id = this.closest('section').getAttribute('aria-labelledby') + '-error';
      this.shares = [Number(this.dataset.shareLow), Number(this.dataset.shareHigh)].map(value => Number.isFinite(value) ? clamp(value, 0, 30) : 0).sort((a, b) => a - b);
      this.querySelector('[data-grc-content]').hidden = false;
      this.addEventListener('input', event => this.handleInput(event), {signal: this.controller.signal});
      this.addEventListener('change', event => {
        if (event.target.tagName === 'SELECT' || event.target.type === 'radio') this.handleInput(event);
      }, {signal: this.controller.signal});
      this.addEventListener('focusout', event => {
        const input = event.target;
        if (input.dataset.grcValue && this.validInput(input)) input.value = this.codec.format(this.codec.parse(input.value));
      }, {signal: this.controller.signal});
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.dataset.grcValue && this.validInput(event.target)) {
          event.target.value = this.codec.format(this.codec.parse(event.target.value));
        }
        const name = event.target.dataset.grcSlider;
        if (!name || !['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const input = this.inputs[name];
        const parsed = this.codec.parse(input.value);
        const value = Number.isFinite(parsed) ? parsed : 0;
        const position = positionFromValue(name, value);
        const step = Math.max(1, Math.abs(valueFromPosition(name, Math.min(1000, position + 1)) - value));
        const direction = ['ArrowLeft', 'ArrowDown', 'PageDown'].includes(event.key) ? -1 : 1;
        const increment = step * (event.key.startsWith('Page') ? 10 : 1);
        input.value = this.codec.format(event.key === 'Home' ? 0 : event.key === 'End' ? scales[name].at(-1) : clamp(value + direction * increment, 0, Number(input.dataset.grcMax)));
        this.render(true);
      }, {signal: this.controller.signal});
      this.querySelector('[data-grc-auto]').addEventListener('click', () => {
        this.manualReach = false;
        this.render(true);
      }, {signal: this.controller.signal});
      this.observer = new ResizeObserver(() => this.fitResult());
      this.observer.observe(this.querySelector('.grc-result-range'));
      this.render(false);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      clearTimeout(this.announcementTimer);
      cancelAnimationFrame(this.fitFrame);
    }

    handleInput(event) {
      const key = event.target.dataset.grcSlider;
      if (key) this.inputs[key].value = this.codec.format(valueFromPosition(key, Number(event.target.value)));
      if (event.target === this.reachInput) this.manualReach = true;
      this.render(true);
    }

    validInput(input) {
      const value = this.codec.parse(input.value);
      return finite(value) && value <= Number(input.dataset.grcMax) && (!input.hasAttribute('data-grc-integer') || Number.isInteger(value));
    }

    paintRange(input) {
      input.style.setProperty('--grc-progress', `${(Number(input.value) - Number(input.min)) / (Number(input.max) - Number(input.min)) * 100}%`);
    }

    money(value, compact = true) {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency', currency: this.currency.value, currencyDisplay: 'narrowSymbol',
        useGrouping: 'always',
        notation: compact && value >= 10000000 ? 'compact' : 'standard',
        maximumFractionDigits: compact && value >= 10000000 ? 1 : 0
      }).format(value);
    }

    moneyRange(range) {
      const [low, high] = range.map(value => this.money(value));
      return low === high ? low : `${low} to ${high}`;
    }

    text(selector, value) {
      this.querySelector(selector).textContent = value;
    }

    fitResult() {
      cancelAnimationFrame(this.fitFrame);
      this.fitFrame = requestAnimationFrame(() => {
        const range = this.querySelector('.grc-result-range');
        range.style.removeProperty('font-size');
        const size = parseFloat(getComputedStyle(range).fontSize);
        if (range.scrollWidth > range.clientWidth) {
          range.style.fontSize = `${Math.max(18, Math.floor(size * (range.clientWidth - 6) / range.scrollWidth))}px`;
        }
      });
    }

    render(announce) {
      const mode = this.querySelector('input[type=radio]:checked').value;
      const conversion = this.querySelector('[data-grc-conversion]').value;
      const exact = mode === 'conversion' && conversion === 'exact';
      this.querySelectorAll('[data-grc-mode-panel]').forEach(panel => {
        panel.hidden = panel.dataset.grcModePanel !== mode;
        panel.querySelectorAll('input,select').forEach(input => { input.disabled = panel.hidden; });
      });
      this.querySelector('[data-grc-exact]').hidden = !exact;
      this.inputs.conversion.disabled = !exact;
      const active = Object.values(this.inputs).filter(input => !input.disabled);
      const invalid = active.filter(input => !this.validInput(input));
      Object.values(this.inputs).forEach(input => {
        const bad = invalid.includes(input);
        input.setAttribute('aria-invalid', String(bad));
        if (bad) input.setAttribute('aria-describedby', this.error.id);
        else input.removeAttribute('aria-describedby');
      });
      this.error.hidden = invalid.length === 0;
      if (invalid.length) {
        this.error.textContent = (this.labels.validation || 'Check the highlighted values to update your estimate.');
        ['[data-grc-low]', '[data-grc-high]', '[data-grc-lift]', '[data-grc-sessions]', '[data-grc-conversion-lift]'].forEach(selector => this.text(selector, '--'));
        this.text('[data-grc-announcement]', (this.labels.validation || 'Check the highlighted values to update your estimate.'));
        clearTimeout(this.announcementTimer);
        return;
      }

      const values = Object.fromEntries(Object.entries(this.inputs).map(([name, input]) => [name, this.codec.parse(input.value)]));
      const number = value => new Intl.NumberFormat(this.locale, {useGrouping: 'always', maximumFractionDigits: 0}).format(value);
      const percent = value => new Intl.NumberFormat(this.locale, {maximumFractionDigits: 1}).format(value);
      Object.entries(this.sliders).forEach(([key, slider]) => {
        slider.value = positionFromValue(key, values[key]);
        slider.setAttribute('aria-valuetext', number(values[key]));
        this.paintRange(slider);
      });
      const suggested = suggestedReach(values.collections);
      if (!this.manualReach) this.reachInput.value = suggested.toFixed(2);
      this.paintRange(this.reachInput);
      this.reachInput.setAttribute('aria-valuetext', `${percent(Number(this.reachInput.value))}%`);
      this.text('[data-grc-reach-label]', percent(Number(this.reachInput.value)));
      this.text('[data-grc-reach-note]', this.manualReach ? `${this.labels.your_share} ${this.labels.suggested}: ${percent(suggested)}%.` : `${this.labels.suggested}: ${percent(suggested)}%. ${this.labels.adjust_share}`);
      this.querySelector('[data-grc-auto]').hidden = !this.manualReach;
      this.text('[data-grc-share-label]', `${percent(this.shares[0])}-${percent(this.shares[1])}%`);
      const symbol = new Intl.NumberFormat(this.locale, {style: 'currency', currency: this.currency.value, currencyDisplay: 'narrowSymbol'}).formatToParts(0).find(part => part.type === 'currency').value;
      this.querySelectorAll('[data-grc-symbol]').forEach(element => {element.textContent = symbol;});

      const rates = exact ? [values.conversion, values.conversion] : conversion.split(',').map(Number);
      const rps = mode === 'rps' ? [values.rps, values.rps] : rates.map(rate => rate / 100 * values.aov);
      const result = calculate({...values, reach: Number(this.reachInput.value), rps, rates: mode === 'conversion' ? rates : undefined}, this.shares);
      this.text('[data-grc-low]', this.money(result.extra[0]));
      this.text('[data-grc-high]', this.money(result.extra[1]));
      this.querySelector('[data-grc-low]').title = this.money(result.extra[0], false);
      this.querySelector('[data-grc-high]').title = this.money(result.extra[1], false);
      this.text('[data-grc-lift]', `+${percent(result.uplift[0])}% ${this.labels.to || 'to'} +${percent(result.uplift[1])}%`);
      this.text('[data-grc-conversion-lift]', `+${percent(result.conversionLift[0])}% ${this.labels.to || 'to'} +${percent(result.conversionLift[1])}%`);
      this.text('[data-grc-sessions]', number(result.sessions));
      this.text('[data-grc-adjusted-share]', `${percent(result.shares[0])}-${percent(result.shares[1])}%`);
      this.text('[data-grc-profile]', `${number(values.products)} ${values.products === 1 ? this.labels.product_one : this.labels.product_other} / ${number(values.collections)} ${values.collections === 1 ? this.labels.collection_one : this.labels.collection_other} / ${number(values.countries)} ${values.countries === 1 ? this.labels.country_one : this.labels.country_other}`);
      this.fitResult();
      if (announce) {
        clearTimeout(this.announcementTimer);
        this.announcementTimer = setTimeout(() => {
          this.text('[data-grc-announcement]', `${this.labels.extra_revenue}: ${this.moneyRange(result.extra)} ${this.currency.value}. ${this.labels.conversion_lift}: ${percent(result.conversionLift[0])}% ${this.labels.to} ${percent(result.conversionLift[1])}%. ${this.labels.influenced_sessions}: ${number(result.sessions)}.`);
        }, 300);
      }
    }
  }

  customElements.define('geosort-revenue-calculator', RevenueCalculator);
})();
